import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type CarouselItem,
  type CzasotekstElements,
  type CzasotekstOverlay,
  type MediaAsset,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MediaThumb } from "@/components/MediaThumb";
import CaptionChatPanel from "@/components/CaptionChatPanel";
import CzasotekstComposeCanvas, { czasotekstVideoUrl } from "@/components/CzasotekstComposeCanvas";
import EpidemicMusicPicker from "@/components/EpidemicMusicPicker";
import { StudioOptionCard } from "@/components/DesignReviewPanel";
import { cn } from "@/lib/utils";

const DEFAULT_ELEMENTS: CzasotekstElements = {
  hook: {
    element_id: "pack:pack_pill_brand:hook",
    frame_id: "pack:pack_pill_brand:hook",
    box: { x: 80, y: 720, w: 920, h: 280, rotate: 0 },
    text: { font: "SourceSans3-Bold", size: 64, color: "#ffffff", align: "center" },
  },
  cta: {
    element_id: "pack:pack_pill_brand:cta",
    frame_id: "pack:pack_pill_brand:cta",
    content: "przeczytaj opis",
    box: { x: 200, y: 1280, w: 680, h: 96, rotate: 0 },
    text: { font: "SourceSans3-Bold", size: 36, color: "#ffffff", align: "center" },
  },
  arrow: {
    element_id: "pack:pack_pill_brand:arrow",
    frame_id: "pack:pack_pill_brand:arrow",
    box: { x: 460, y: 1380, w: 140, h: 90, rotate: 0 },
    text: { font: "SourceSans3-Bold", size: 36, color: "#ffffff", align: "center" },
  },
};

function fitBoxToNatural(
  natW: number,
  natH: number,
  opts: { maxW: number; maxH: number; y: number; canvasW?: number },
): { x: number; y: number; w: number; h: number; rotate: number } {
  const canvasW = opts.canvasW ?? 1080;
  const aspect = natW / Math.max(1, natH);
  let w = opts.maxW;
  let h = Math.round(w / aspect);
  if (h > opts.maxH) {
    h = opts.maxH;
    w = Math.round(h * aspect);
  }
  w = Math.max(120, w);
  h = Math.max(64, h);
  const x = Math.round((canvasW - w) / 2);
  return { x, y: opts.y, w, h, rotate: 0 };
}

function applyHookFrameComplete(
  prev: CzasotekstElements,
  frame: {
    id: string;
    style_id?: string;
    element_id?: string;
    cta_element_id?: string;
    cta_layout?: string;
    width?: number;
    height?: number;
    cta_width?: number;
    cta_height?: number;
  },
): CzasotekstElements {
  const eid = frame.element_id || `hookframe:${frame.id}`;
  const ctaEid = frame.cta_element_id || `hookframe:${frame.id}:cta`;
  const dark = frame.style_id === "paper-ink" || frame.style_id === "pill-dark";
  const ink = dark ? "#f8f5ef" : "#281f1c";
  const layout = (frame.cta_layout || "under").toLowerCase();
  const hookNatW = Number(frame.width || 0);
  const hookNatH = Number(frame.height || 0);
  const ctaNatW = Number(frame.cta_width || 0);
  const ctaNatH = Number(frame.cta_height || 0);
  const prevHook = prev.hook?.box;
  const hookBox =
    hookNatW > 0 && hookNatH > 0
      ? fitBoxToNatural(hookNatW, hookNatH, {
          maxW: 920,
          maxH: 360,
          y: prevHook?.y ?? 720,
        })
      : prevHook || { x: 80, y: 720, w: 920, h: 280, rotate: 0 };
  const ctaBox =
    ctaNatW > 0 && ctaNatH > 0
      ? fitBoxToNatural(ctaNatW, ctaNatH, {
          maxW: layout === "sides" ? 920 : 800,
          maxH: layout === "sides" ? 160 : 240,
          y: layout === "sides" ? 1500 : 1460,
        })
      : layout === "sides"
        ? { x: 120, y: 1500, w: 840, h: 120, rotate: 0 }
        : { x: 160, y: 1460, w: 760, h: 200, rotate: 0 };
  return {
    ...prev,
    hook: {
      ...prev.hook,
      element_id: eid,
      frame_id: eid,
      visible: true,
      box: hookBox,
      text: {
        ...(prev.hook?.text || {}),
        color: ink,
      },
    },
    cta: {
      ...prev.cta,
      element_id: ctaEid,
      frame_id: ctaEid,
      visible: true,
      content: prev.cta?.content?.trim() || "przeczytaj opis",
      box: ctaBox,
      text: {
        ...(prev.cta?.text || {}),
        color: ink,
        size: prev.cta?.text?.size || 36,
      },
    },
    arrow: {
      ...prev.arrow,
      visible: false,
    },
  };
}

function mergeElements(
  raw?: CzasotekstElements | null,
  overlay?: CzasotekstOverlay | null,
): CzasotekstElements {
  const hookSrc = raw?.hook || overlay || DEFAULT_ELEMENTS.hook;
  return {
    hook: {
      ...DEFAULT_ELEMENTS.hook,
      ...hookSrc,
      box: { ...DEFAULT_ELEMENTS.hook!.box!, ...(hookSrc?.box || {}) },
      text: { ...DEFAULT_ELEMENTS.hook!.text, ...(hookSrc?.text || {}) },
    },
    cta: {
      ...DEFAULT_ELEMENTS.cta,
      ...(raw?.cta || {}),
      content: String(raw?.cta?.content || DEFAULT_ELEMENTS.cta!.content || "przeczytaj opis"),
      box: { ...DEFAULT_ELEMENTS.cta!.box!, ...(raw?.cta?.box || {}) },
      text: { ...DEFAULT_ELEMENTS.cta!.text, ...(raw?.cta?.text || {}) },
    },
    arrow: {
      ...DEFAULT_ELEMENTS.arrow,
      ...(raw?.arrow || {}),
      box: { ...DEFAULT_ELEMENTS.arrow!.box!, ...(raw?.arrow?.box || {}) },
      text: { ...DEFAULT_ELEMENTS.arrow!.text, ...(raw?.arrow?.text || {}) },
    },
  };
}

function fmt(sec: number) {
  const n = Math.max(0, Number(sec) || 0);
  const m = Math.floor(n / 60);
  const s = (n - m * 60).toFixed(1);
  return m ? `${m}:${s.padStart(4, "0")}` : `${Number(s).toFixed(1)}s`;
}

type ComposeStep = "film" | "kadr" | "sklad";

export default function CzasotekstPanel({
  item,
  caption,
  onCaption,
  onHook,
  section = "content",
}: {
  item: CarouselItem;
  caption: string;
  onCaption: (v: string) => void;
  onHook: (v: string) => void;
  /** content = hak/opis/czat; compose = film + skład MP4 */
  section?: "content" | "compose";
}) {
  const qc = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const seekTargetRef = useRef<"in" | "out">("in");
  const clipPlayCleanupRef = useRef<(() => void) | null>(null);
  const clip = (item.copy as { czasotekst?: Record<string, unknown> } | undefined)?.czasotekst || {};
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<MediaAsset | null>(null);
  const [inSec, setInSec] = useState(Number(clip.in_sec || 0));
  const [outSec, setOutSec] = useState(Number(clip.out_sec || 6));
  const [scale, setScale] = useState(Number(clip.scale || 1));
  const [panX, setPanX] = useState(Number(clip.x || 0));
  const [panY, setPanY] = useState(Number(clip.y || 0));
  const [loopSec, setLoopSec] = useState(Number(clip.loop_sec || 7));
  const [bedId, setBedId] = useState(String(clip.bed_id || ""));
  const [epidemicTrackId, setEpidemicTrackId] = useState(String(clip.epidemic_track_id || ""));
  const [epidemicLabel, setEpidemicLabel] = useState(
    String(item.reel_track_title || (clip.epidemic_track_id ? clip.epidemic_track_id : "") || ""),
  );
  const [crossfadeSec, setCrossfadeSec] = useState(Number(clip.audio_crossfade_sec || 1.5));
  const [videoCrossfadeSec, setVideoCrossfadeSec] = useState(
    Number(clip.video_crossfade_sec ?? 0.5),
  );
  const [hook, setHook] = useState(String((item.copy as { hook_on_screen?: string })?.hook_on_screen || item.hook || ""));
  const [composeStep, setComposeStep] = useState<ComposeStep>(() =>
    clip.asset_id ? "kadr" : "film",
  );
  const [clipPlaying, setClipPlaying] = useState(false);
  const [elements, setElements] = useState<CzasotekstElements>(() => {
    const clipEl = (clip as { elements?: CzasotekstElements; overlay?: CzasotekstOverlay }).elements;
    const clipOv = (clip as { overlay?: CzasotekstOverlay }).overlay;
    return mergeElements(clipEl, clipOv);
  });
  const [packId, setPackId] = useState(String((clip as { pack_id?: string }).pack_id || "pack_pill_brand"));
  const listQ = useQuery({
    queryKey: ["media-reels", q],
    queryFn: () => api.mediaList({ kind: "video", q }),
  });
  const catalogQ = useQuery({
    queryKey: ["carousel-catalog"],
    queryFn: () => api.carouselCatalog(),
    staleTime: 60_000,
  });
  const musicStatusQ = useQuery({
    queryKey: ["carousel-music-status"],
    queryFn: api.carouselMusicStatus,
    staleTime: 60_000,
    retry: false,
  });
  const reels = useMemo(() => listQ.data?.items || [], [listQ.data]);
  const packs = catalogQ.data?.czasotekst_packs || [];
  const hookFrameStyles = catalogQ.data?.czasotekst_hook_frame_styles || [];
  const hookFrames = catalogQ.data?.czasotekst_hook_frames || [];
  const beds = catalogQ.data?.czasotekst_beds || [];
  const ffmpeg = listQ.data?.ffmpeg !== false;
  const music = musicStatusQ.data;
  const activeAssetId = picked?.id || String(clip.asset_id || "");
  const videoUrl = activeAssetId ? czasotekstVideoUrl(activeAssetId) : "";

  const pickAsset = (asset: MediaAsset) => {
    setPicked(asset);
    const dur = Number(asset.duration_sec || 0);
    setInSec(0);
    setOutSec(dur >= 3 ? Math.min(dur, 6) : Math.max(dur, 0.2));
    setComposeStep("kadr");
  };

  useEffect(() => {
    const id = String(clip.asset_id || "");
    if (!id) return;
    if (picked?.id === id) return;
    const fromList = reels.find((a) => a.id === id);
    if (fromList) {
      setPicked(fromList);
      return;
    }
    let cancelled = false;
    void api.mediaGet(id).then(
      (asset) => {
        if (!cancelled) setPicked(asset);
      },
      () => {
        if (!cancelled) {
          setPicked({
            id,
            original_name: id.slice(0, 8),
            duration_sec: Number(clip.out_sec || 6),
          } as MediaAsset);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [clip.asset_id, clip.out_sec, picked?.id, reels]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || composeStep !== "kadr") return;
    const t = seekTargetRef.current === "out" ? outSec : inSec;
    try {
      if (Math.abs(v.currentTime - t) > 0.04) v.currentTime = Math.max(0, t);
    } catch {
      /* ignore seek race before metadata */
    }
  }, [inSec, outSec, composeStep, activeAssetId]);

  const stopClipPreview = () => {
    clipPlayCleanupRef.current?.();
    clipPlayCleanupRef.current = null;
    const v = videoRef.current;
    if (v) v.pause();
    setClipPlaying(false);
  };

  const playClipPreview = () => {
    const v = videoRef.current;
    if (!v) return;
    stopClipPreview();
    seekTargetRef.current = "in";
    try {
      v.currentTime = Math.max(0, inSec);
    } catch {
      /* ignore */
    }
    const onTime = () => {
      if (v.currentTime >= outSec - 0.05) {
        stopClipPreview();
        seekTargetRef.current = "out";
        try {
          v.currentTime = Math.max(0, outSec);
        } catch {
          /* ignore */
        }
      }
    };
    v.addEventListener("timeupdate", onTime);
    clipPlayCleanupRef.current = () => v.removeEventListener("timeupdate", onTime);
    setClipPlaying(true);
    void v.play().catch(() => {
      stopClipPreview();
    });
  };

  useEffect(
    () => () => {
      clipPlayCleanupRef.current?.();
      clipPlayCleanupRef.current = null;
      videoRef.current?.pause();
    },
    [],
  );

  const applyCopyResult = (data: CarouselItem) => {
    qc.setQueryData(["carousel", item.id], data);
    const nextHook = String((data.copy as { hook_on_screen?: string })?.hook_on_screen || data.hook || "");
    setHook(nextHook);
    onHook(nextHook);
    onCaption(data.caption || "");
  };

  const copyM = useMutation({
    mutationFn: (feedback: string) => api.czasotekstCopy(item.id, feedback),
    onSuccess: applyCopyResult,
  });

  const chatM = useMutation({
    mutationFn: ({ message, apply = false }: { message: string; apply?: boolean }) =>
      api.czasotekstChat(item.id, message, apply),
    onSuccess: applyCopyResult,
  });

  const undoChatM = useMutation({
    mutationFn: () => api.carouselCaptionChatUndo(item.id),
    onSuccess: applyCopyResult,
  });

  const generating = copyM.isPending || item.llm_status === "czasotekst_copy";
  const chatting = chatM.isPending || item.llm_status === "czasotekst_chat";
  const writing = generating || chatting;
  const chatMessages = Array.isArray(item.discussion?.caption_chat) ? item.discussion.caption_chat : [];
  const canUndoChat = Boolean(item.discussion?.caption_chat_undo);
  const pending = item.discussion?.caption_chat_pending || null;
  const pendingHook = String(pending?.hook_on_screen || "").trim();
  const pendingCaption = String(pending?.caption || "").trim();
  const pendingEngage = String(pending?.captionEngage || "").trim();
  const hasPending = Boolean(pendingHook || pendingCaption || pendingEngage);

  useEffect(() => {
    if (writing) return;
    const next = String((item.copy as { hook_on_screen?: string } | undefined)?.hook_on_screen || item.hook || "");
    if (next) setHook(next);
  }, [item.id, item.hook, item.copy, writing]);

  // Tylko przy zmianie karuzeli. Nie na każde item.copy (invalidate po zapisie
  // nadpisywało lokalne boxy zanim request wziął świeży stan z canvasu).
  useEffect(() => {
    const ct = (item.copy as { czasotekst?: { elements?: CzasotekstElements; overlay?: CzasotekstOverlay; pack_id?: string } } | undefined)
      ?.czasotekst;
    if (!ct) return;
    setElements(mergeElements(ct.elements, ct.overlay));
    if (ct.pack_id) setPackId(ct.pack_id);
  }, [item.id]);

  const clipBody = (compose: boolean, els?: CzasotekstElements) => {
    if (!picked?.id && !clip.asset_id) throw new Error("wybierz film");
    const live = els || elements;
    return {
      asset_id: picked?.id || String(clip.asset_id || ""),
      in_sec: inSec,
      out_sec: outSec,
      scale,
      x: panX,
      y: panY,
      loop_sec: loopSec,
      hook_on_screen: hook,
      overlay: live.hook,
      elements: live,
      pack_id: packId,
      bed_id: bedId || beds.find((b) => b.default)?.id || beds[0]?.id || "",
      epidemic_track_id: epidemicTrackId || "",
      epidemic_track_title: epidemicLabel || "",
      audio_crossfade_sec: crossfadeSec,
      video_crossfade_sec: videoCrossfadeSec,
      compose,
    };
  };

  const composeM = useMutation({
    mutationFn: (els?: CzasotekstElements) => api.czasotekstCompose(item.id, clipBody(true, els)),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      const ct = (data.copy as { czasotekst?: { elements?: CzasotekstElements; overlay?: CzasotekstOverlay; pack_id?: string } } | undefined)
        ?.czasotekst;
      if (ct?.elements) {
        setElements(mergeElements(ct.elements, ct.overlay));
        if (ct.pack_id) setPackId(ct.pack_id);
      }
    },
  });

  const saveOverlayM = useMutation({
    mutationFn: (els?: CzasotekstElements) => api.czasotekstCompose(item.id, clipBody(false, els)),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      const ct = (data.copy as { czasotekst?: { elements?: CzasotekstElements; overlay?: CzasotekstOverlay; pack_id?: string } } | undefined)
        ?.czasotekst;
      if (ct?.elements) {
        setElements(mergeElements(ct.elements, ct.overlay));
        if (ct.pack_id) setPackId(ct.pack_id);
      }
    },
  });

  const regenPackM = useMutation({
    mutationFn: (pid: string) => api.czasotekstPacksGenerate({ pack_id: pid }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel-catalog"] });
    },
  });

  const genHookFrameM = useMutation({
    mutationFn: (body: {
      style_id?: string;
      custom_prompt?: string;
      shadow?: boolean;
      cta_layout?: "under" | "sides" | "auto";
    }) => api.czasotekstHookFramesGenerate(body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["carousel-catalog"] });
      const frame = data.frame;
      if (!frame?.id) return;
      setElements((prev) => applyHookFrameComplete(prev, frame));
    },
  });

  const starHookFrameM = useMutation({
    mutationFn: ({ id, starred }: { id: string; starred: boolean }) =>
      api.czasotekstHookFramesPatch(id, { starred }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel-catalog"] });
    },
  });

  const saveCaptionM = useMutation({
    mutationFn: () =>
      api.carouselPatchCopy(item.id, {
        caption,
        copy: {
          ...(item.copy || {}),
          hook_on_screen: hook,
          caption,
        },
      }),
    onSuccess: (data) => qc.setQueryData(["carousel", item.id], data),
  });

  const approveM = useMutation({
    mutationFn: () => api.carouselApproveCopy(item.id),
    onSuccess: (data) => {
      applyCopyResult(data);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
      qc.invalidateQueries({ queryKey: ["carousels"] });
    },
  });

  // Szkic z Wejścia: caption bywa pusty, a draft_text już jest. Podpowiedz go w polu opisu.
  useEffect(() => {
    if (caption.trim()) return;
    const draft = String(item.draft_text || "").trim();
    if (draft) onCaption(draft);
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps -- tylko przy nowym itemie

  return (
    <div
      className={cn(
        "grid min-w-0 gap-4",
        section === "content" ? "xl:grid-cols-12" : "grid-cols-1",
      )}
    >
      {section === "content" ? (
      <div className="min-w-0 space-y-4 xl:col-span-4">
          <div id="czasotekst-text">
            <StudioOptionCard
              optional={false}
              title="Tekst czasotekstu"
              hint="Główna treść jest w opisie do czytania. Na ekranie tylko krótki hak."
            >
              <div className="space-y-2">
                {item.czasotekst_variant_id || item.czasotekst_form_id ? (
                  <p className="text-[11px] text-muted-foreground">
                    {[
                      item.czasotekst_form_id ? `Forma: ${item.czasotekst_form_id}` : "",
                      item.czasotekst_variant_id ? `Hipoteza: ${item.czasotekst_variant_id}` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    {" · "}
                    zmiana w Briefie
                  </p>
                ) : null}
                <label className="block text-[11px] text-muted-foreground">Hak na ekranie (max ~12 słów)</label>
                <input
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  value={hook}
                  onChange={(e) => {
                    setHook(e.target.value);
                    onHook(e.target.value);
                  }}
                />
                <label className="block text-[11px] text-muted-foreground">Opis (caption) - to czyta widz</label>
                <textarea
                  className="min-h-[160px] w-full rounded border px-2 py-1.5 text-sm"
                  value={caption}
                  onChange={(e) => onCaption(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="brand" disabled={writing} onClick={() => copyM.mutate("")}>
                    {generating ? "Piszę…" : "Wygeneruj hak + opis"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={saveCaptionM.isPending}
                    onClick={() => saveCaptionM.mutate()}
                  >
                    Zapisz tekst
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!caption.trim() || approveM.isPending}
                    onClick={() => approveM.mutate()}
                  >
                    Zatwierdź tekst
                  </Button>
                </div>
                {copyM.isError ? (
                  <p className="text-xs text-destructive">{(copyM.error as Error)?.message || "błąd"}</p>
                ) : null}
              </div>
            </StudioOptionCard>
          </div>
      </div>
      ) : null}

      {section === "compose" ? (
      <div className="min-w-0 space-y-4" id="czasotekst-film-wrap">
          <div id="czasotekst-film">
            <StudioOptionCard
              optional={false}
              title="Film i skład ekranu"
              hint="3 kroki: film → kadr → skład. Render wypala ramkę do MP4."
            >
              {!ffmpeg ? (
                <p className="text-xs text-destructive">Na serwerze nie ma ffmpeg. Cięcie nie zadziała.</p>
              ) : null}
              <div className="mb-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={composeStep === "film" ? "brand" : "outline"}
                  onClick={() => setComposeStep("film")}
                >
                  Film
                </Button>
                <Button
                  size="sm"
                  variant={composeStep === "kadr" ? "brand" : "outline"}
                  disabled={!activeAssetId}
                  onClick={() => setComposeStep("kadr")}
                >
                  Kadr
                </Button>
                <Button
                  size="sm"
                  variant={composeStep === "sklad" ? "brand" : "outline"}
                  disabled={!activeAssetId}
                  onClick={() => setComposeStep("sklad")}
                >
                  Skład
                </Button>
              </div>
              {composeStep === "film" ? (
                <div className="space-y-3">
                  <p className="text-[11px] text-muted-foreground">
                    Wybierz film z biblioteki. Po wyborze przejdziesz do kadru (wycinek + zoom).
                  </p>
                  <input
                    className="w-full rounded border px-2 py-1 text-xs"
                    placeholder="szukaj w bibliotece wideo"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                  <div className="grid max-h-[min(28rem,60vh)] grid-cols-3 gap-2 overflow-auto sm:grid-cols-4">
                    {reels.map((asset) => (
                      <button
                        key={asset.id}
                        type="button"
                        className={cn(
                          "overflow-hidden rounded border text-left",
                          activeAssetId === asset.id ? "border-brand" : "border-border",
                        )}
                        onClick={() => pickAsset(asset)}
                      >
                        <MediaThumb asset={asset} />
                        <p className="truncate px-1 py-0.5 text-[10px]">
                          {asset.original_name || asset.id.slice(0, 8)}
                        </p>
                      </button>
                    ))}
                  </div>
                  {!reels.length ? (
                    <p className="text-xs text-muted-foreground">
                      Brak wideo. Wgraj w Media → Wideo (albo inbox/rolki).
                    </p>
                  ) : null}
                  {activeAssetId ? (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="brand" onClick={() => setComposeStep("kadr")}>
                        Dalej: kadr
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {composeStep === "kadr" ? (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)] lg:items-start">
                  <div className="min-w-0 space-y-3">
                    {picked || clip.asset_id ? (
                      <div className="space-y-2">
                        <p className="text-[11px] text-muted-foreground">
                          {picked?.original_name || activeAssetId.slice(0, 8)} · ustaw wycinek, zoom i muzykę
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <label className="text-[11px]">
                            Start {fmt(inSec)}
                            <input
                              type="range"
                              min={0}
                              max={Math.max(0.1, Number(picked?.duration_sec || outSec))}
                              step={0.1}
                              value={inSec}
                              className="w-full"
                              onChange={(e) => {
                                seekTargetRef.current = "in";
                                stopClipPreview();
                                setInSec(Number(e.target.value));
                              }}
                            />
                          </label>
                          <label className="text-[11px]">
                            Koniec {fmt(outSec)}
                            <input
                              type="range"
                              min={inSec + 0.2}
                              max={Math.max(inSec + 0.2, Number(picked?.duration_sec || outSec + 1))}
                              step={0.1}
                              value={outSec}
                              className="w-full"
                              onChange={(e) => {
                                seekTargetRef.current = "out";
                                stopClipPreview();
                                setOutSec(Number(e.target.value));
                              }}
                            />
                          </label>
                          <label className="text-[11px]">
                            Zbliżenie {scale.toFixed(2)}×
                            <input
                              type="range"
                              min={1}
                              max={2.5}
                              step={0.05}
                              value={scale}
                              className="w-full"
                              onChange={(e) => setScale(Number(e.target.value))}
                            />
                          </label>
                          <label className="text-[11px]">
                            Pętla {loopSec.toFixed(0)} s
                            <input
                              type="range"
                              min={3}
                              max={15}
                              step={1}
                              value={loopSec}
                              className="w-full"
                              onChange={(e) => setLoopSec(Number(e.target.value))}
                            />
                          </label>
                          <label className="text-[11px]">
                            Pozycja X {panX}
                            <input
                              type="range"
                              min={-400}
                              max={400}
                              step={10}
                              value={panX}
                              className="w-full"
                              onChange={(e) => setPanX(Number(e.target.value))}
                            />
                          </label>
                          <label className="text-[11px]">
                            Pozycja Y {panY}
                            <input
                              type="range"
                              min={-400}
                              max={400}
                              step={10}
                              value={panY}
                              className="w-full"
                              onChange={(e) => setPanY(Number(e.target.value))}
                            />
                          </label>
                          <label className="text-[11px] sm:col-span-2">
                            Muzyka lokalna (fallback, gdy brak Epidemic)
                            <select
                              className="mt-0.5 w-full rounded border px-1.5 py-1 text-xs"
                              value={bedId || beds.find((b) => b.default)?.id || beds[0]?.id || ""}
                              onChange={(e) => setBedId(e.target.value)}
                              disabled={Boolean(epidemicTrackId)}
                            >
                              {beds.map((bed) => (
                                <option key={bed.id} value={bed.id}>
                                  {bed.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="sm:col-span-2 space-y-1">
                            <p className="text-[11px] text-muted-foreground">
                              Epidemic Sound (priorytet nad soft_pad przy składzie)
                            </p>
                            <EpidemicMusicPicker
                              music={music}
                              selectedId={epidemicTrackId}
                              selectedLabel={epidemicLabel}
                              allowClear
                              onSelect={(track) => {
                                if (!track) {
                                  setEpidemicTrackId("");
                                  setEpidemicLabel("");
                                  return;
                                }
                                setEpidemicTrackId(track.id);
                                setEpidemicLabel(`${track.title || ""} · ${track.artist || ""}`.trim());
                              }}
                            />
                          </div>
                          <label className="text-[11px] sm:col-span-2">
                            Płynność pętli muzyki {crossfadeSec.toFixed(1)} s
                            <input
                              type="range"
                              min={0.3}
                              max={3}
                              step={0.1}
                              value={crossfadeSec}
                              className="w-full"
                              onChange={(e) => setCrossfadeSec(Number(e.target.value))}
                            />
                          </label>
                          <label className="text-[11px] sm:col-span-2">
                            Płynność pętli obrazu {videoCrossfadeSec.toFixed(1)} s
                            <input
                              type="range"
                              min={0}
                              max={2}
                              step={0.1}
                              value={videoCrossfadeSec}
                              className="w-full"
                              onChange={(e) => setVideoCrossfadeSec(Number(e.target.value))}
                            />
                          </label>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => setComposeStep("film")}>
                            Zmień film
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!activeAssetId}
                            onClick={() => setComposeStep("sklad")}
                          >
                            Dalej: skład
                          </Button>
                          <Button
                            size="sm"
                            variant="brand"
                            disabled={composeM.isPending || !ffmpeg}
                            onClick={() => composeM.mutate(undefined)}
                          >
                            {composeM.isPending ? "Składam MP4…" : "Złóż czasotekst (MP4)"}
                          </Button>
                        </div>
                        {composeM.isError ? (
                          <p className="text-xs text-destructive">
                            {(composeM.error as Error)?.message || "błąd składu"}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Najpierw wybierz film w kroku Film.
                      </p>
                    )}
                  </div>
                  <aside className="min-w-0 space-y-2 lg:sticky lg:top-4">
                    {activeAssetId ? (
                      <div className="space-y-2">
                        <p className="text-[11px] font-medium text-slate-800">Podgląd wycinka</p>
                        <div
                          className="relative mx-auto w-full max-w-[280px] overflow-hidden rounded border bg-black shadow-sm"
                          style={{ aspectRatio: "9 / 16" }}
                        >
                          <video
                            ref={videoRef}
                            key={activeAssetId}
                            className="absolute inset-0 h-full w-full object-cover"
                            src={api.mediaFile(activeAssetId, false)}
                            muted
                            playsInline
                            style={{
                              transform: `scale(${scale}) translate(${panX / scale}px, ${panY / scale}px)`,
                            }}
                          />
                        </div>
                        <p className="text-center text-[10px] text-muted-foreground">
                          Start {fmt(inSec)} – Koniec {fmt(outSec)}
                        </p>
                        <div className="flex justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => (clipPlaying ? stopClipPreview() : playClipPreview())}
                          >
                            {clipPlaying ? "Stop" : "Odtwórz wycinek"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="rounded border border-dashed border-border px-2 py-6 text-center text-[11px] text-muted-foreground">
                        Wybierz film, żeby zobaczyć podgląd.
                      </p>
                    )}
                  </aside>
                </div>
              ) : null}
              {composeStep === "sklad" ? (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(260px,340px)] lg:items-start">
                  <div className="min-w-0 space-y-3">
                    <p className="text-[11px] text-muted-foreground">
                      1) wybierz wygląd → 2) ustaw na filmie → 3) złóż MP4
                    </p>
                    <CzasotekstComposeCanvas
                      packs={packs}
                      packId={packId}
                      elements={elements}
                      overlay={elements.hook}
                      hook={hook}
                      videoUrl={videoUrl}
                      videoAsset={picked}
                      scalePan={{ scale, x: panX, y: panY }}
                      onChange={setElements}
                      onPackChange={(pid, next) => {
                        setPackId(pid);
                        setElements(next);
                      }}
                      onSave={(els) => {
                        setElements(els);
                        saveOverlayM.mutate(els);
                      }}
                      onCompose={(els) => {
                        setElements(els);
                        composeM.mutate(els);
                      }}
                      onRegeneratePack={(pid) => regenPackM.mutate(pid)}
                      regenerating={regenPackM.isPending}
                      saving={saveOverlayM.isPending}
                      composing={composeM.isPending}
                      ffmpegOk={ffmpeg}
                      hookFrameStyles={hookFrameStyles}
                      hookFrames={hookFrames}
                      onGenerateHookFrame={(body) => genHookFrameM.mutate(body)}
                      generatingHookFrame={genHookFrameM.isPending}
                      onStarHookFrame={(id, starred) => starHookFrameM.mutate({ id, starred })}
                      onSelectHookFrame={(frame) => {
                        setElements((prev) => applyHookFrameComplete(prev, frame));
                      }}
                    />
                    {(
                      composeM.isError ||
                      saveOverlayM.isError ||
                      regenPackM.isError ||
                      genHookFrameM.isError
                    ) ? (
                      <p className="text-xs text-destructive">
                        {(
                          (composeM.error ||
                            saveOverlayM.error ||
                            regenPackM.error ||
                            genHookFrameM.error) as Error
                        )?.message || "błąd zapisu / składu"}
                      </p>
                    ) : null}
                  </div>
                  <aside className="min-w-0 space-y-3 lg:sticky lg:top-4">
                    {item.has_reel ? (
                      <div className="space-y-1">
                        <p className="text-[11px] font-medium text-slate-800">Gotowy MP4</p>
                        <p className="text-[10px] text-muted-foreground">
                          Pętla: styk koniec↔start (crossfade audio + obraz).
                        </p>
                        <video
                          className="max-h-[min(28rem,70vh)] w-full rounded border bg-black object-contain"
                          src={`${api.carouselReelUrl(item.id)}?v=${item.updated_at || item.reel_status || ""}`}
                          controls
                          loop
                          playsInline
                        />
                      </div>
                    ) : (
                      <p className="rounded border border-dashed border-border px-2 py-6 text-center text-[11px] text-muted-foreground">
                        Tu pojawi się gotowy MP4 po złożeniu.
                      </p>
                    )}
                  </aside>
                </div>
              ) : null}
            </StudioOptionCard>
          </div>
      </div>
      ) : null}

      {section === "content" ? (
      <>
      <aside id="czasotekst-proposal" className="min-w-0 xl:col-span-4 xl:sticky xl:top-4 xl:self-start">
        <div
          className={cn(
            "flex min-h-[min(28rem,calc(100vh-8rem))] flex-col rounded-md border bg-white p-3 shadow-sm",
            hasPending ? "border-violet-200" : "border-slate-200",
          )}
        >
          <div className="border-b border-border pb-2">
            <p className="text-sm font-medium text-slate-900">Propozycja</p>
            <p className="text-[11px] text-muted-foreground">
              Podgląd zmian z czatu. Do pól po lewej trafia dopiero po Wdrażaj.
            </p>
          </div>
          {hasPending ? (
            <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-auto text-sm">
              {pendingHook ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">Hak</p>
                  <p className="mt-1 rounded-md border border-violet-100 bg-violet-50/80 px-2 py-1.5 text-violet-950">
                    {pendingHook}
                  </p>
                </div>
              ) : null}
              {pendingCaption ? (
                <div className="min-h-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">Opis</p>
                  <p className="mt-1 whitespace-pre-wrap rounded-md border border-violet-100 bg-violet-50/80 px-2 py-1.5 text-violet-950">
                    {pendingCaption}
                  </p>
                </div>
              ) : null}
              {pendingEngage ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">Engage</p>
                  <p className="mt-1 text-xs text-violet-900/80">{pendingEngage}</p>
                </div>
              ) : null}
              {!pendingHook && !pendingCaption ? (
                <p className="text-sm text-muted-foreground">Propozycja bez treści pól.</p>
              ) : null}
              <div className="mt-auto flex flex-wrap gap-2 border-t border-border pt-3">
                <Button
                  size="sm"
                  variant="brand"
                  disabled={writing}
                  onClick={() => chatM.mutate({ message: "wdrażaj", apply: true })}
                >
                  Wdrażaj
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={writing}
                  onClick={() => chatM.mutate({ message: "jeszcze nie" })}
                >
                  Jeszcze nie
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-6 text-sm text-muted-foreground">
              Tu pojawi się propozycja haka lub opisu po wiadomości w czacie. Nic jeszcze nie czeka na akceptację.
            </p>
          )}
        </div>
      </aside>

      <aside id="czasotekst-chat" className="min-w-0 xl:col-span-4 xl:sticky xl:top-4 xl:self-start">
          <CaptionChatPanel
            caption={caption}
            engage={item.caption_engage || ""}
            fbEnding=""
            igEnding=""
            messages={chatMessages}
            verifyCta={false}
            busy={chatting}
            canUndo={canUndoChat}
            disabled={generating}
            llmProgress={item.llm_progress}
            llmStatus={item.llm_status}
            error={
              (chatM.isError && (chatM.error as Error)?.message) ||
              (undoChatM.isError && (undoChatM.error as Error)?.message) ||
              ""
            }
            onCaptionChange={onCaption}
            onVerifyCta={() => undefined}
            onSend={(message) => chatM.mutate({ message })}
            onUndo={() => undoChatM.mutate()}
            undoPending={undoChatM.isPending}
            showCorpus={false}
            showVerify={false}
            title="Czat o treści"
            hint="Najpierw propozycja w środkowej kolumnie. Wdrażam dopiero gdy powiesz tak / Wdrażaj."
            placeholder={
              caption.trim() || hook.trim()
                ? "Np. „zostaw opis, hak: Mama rzuca przy stole…” albo „pasuje”."
                : "Najpierw Wygeneruj hak + opis po lewej, albo napisz kąt tutaj."
            }
            busyLabel="Myślę…"
          />
      </aside>
      </>
      ) : null}
    </div>
  );
}
